import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getLocationsList,
  getProductStateList,
  getProductsList,
  productsByUser,
  transferProduct,
  transportProduct,
  updateProductDB,
  getProductLotList,
  archiveLot,
  unarchiveLot
} from '../Redux/products/slice';
import { saveProperties, saveDraftProperties, deleteDraftProperties } from '../Redux/properties/PropertiesSlice';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { 
  Tabs, 
  Group, 
  TextInput, 
  ActionIcon, 
  Button, 
  Table, 
  Card, 
  Text, 
  Badge, 
  Menu, 
  Tooltip, 
  Paper, 
  ScrollArea, 
  Box, 
  Title, 
  Center, 
  Divider,
  Stack,
  SegmentedControl,
  Grid,
  Avatar,
  Pagination,
  NativeSelect
} from '@mantine/core';

// Instead of using @mantine/modals, we'll use a standard modal from your existing components
// Assuming you have RemoveFieldConfirmationModal or similar that we can reuse
import { 
  IconRefresh, 
  IconPlus, 
  IconSearch, 
  IconFilter, 
  IconEye, 
  IconEdit, 
  IconArrowsLeftRight, 
  IconTruckDelivery, 
  IconArchive, 
  IconTrash,
  IconFileText,
  IconDownload,
  IconDotsVertical,
  IconChevronDown,
  IconChevronUp,
  IconSelector,
  IconPhoto,
  IconLayoutList,
  IconLayoutGrid,
  IconQrcode,
  IconExternalLink
} from '@tabler/icons-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../Redux/apiConfig';
import { toast } from 'react-toastify';
import { ToastProperties } from '../utils/constants/toastProperties';

// Import your existing modals
import ProductPanel from './ProductPanel';
import TransportationModal from './TransportationModal';
import LoadImageModal from '../components/LoadImageModal';
import RemoveFieldConfirmationModal from '../components/RemoveFieldConfirmationModal';
import CreatePropertiesModal from '../components/CreatePropertiesModal';
import ImageUploadModal from './ImageUploadModal';
import DocumentUploadModal from './DocumentUploadModal';

// Constants for product states
const PRODUCT_STATES = {
  ACTIVE: 'active',
  DRAFT: 'draft',
  ARCHIVED: 'archived'
};

const VIEW_MODES = {
  TABLE: 'table',
  CARDS: 'cards'
};

const BLOB_IMG = 'https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/images';

function ProductCardImage({ product }) {
  const png = `${BLOB_IMG}/${product.productID}.png`;
  const jpg = `${BLOB_IMG}/${product.product_name_id}.jpg`;
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r1 = await fetch(png, { method: 'GET' });
        if (!cancelled) {
          if (r1.ok) { setSrc(png); return; }
          const r2 = await fetch(jpg, { method: 'GET' });
          if (!cancelled) setSrc(r2.ok ? jpg : '');
        }
      } catch { if (!cancelled) setSrc(''); }
    })();
    return () => { cancelled = true; };
  }, [png, jpg]);

  if (src === null) {
    return <div style={{ height: 140, backgroundColor: '#f0f0f0' }} />;
  }
  if (!src) {
    return (
      <div style={{ height: 140, backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: '2rem' }}>📦</span>
      </div>
    );
  }
  return (
    <img src={src} alt={product.productName}
      style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
    />
  );
}

export default function ImprovedProductManagement() {
  const dispatch = useDispatch();
  const intl = useIntl();
  
  // Redux state
  const dataLot = useSelector((state) => state.products.productLotList) || [] ;
  const locationList = useSelector((state) => state.products.locationsList);
  const productsList = useSelector((state) => state.products.productsList);
  const selectedLanguage = useSelector((state) => state.language);
  
  // Component state
  const [activeTab, setActiveTab] = useState(PRODUCT_STATES.ACTIVE);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState(VIEW_MODES.TABLE);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('productsPerPage');
    return saved ? parseInt(saved, 10) : 10;
  });
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Modals state
  const [showProductPanel, setShowProductPanel] = useState(false);
  const [showTransportModal, setShowTransportModal] = useState(false);
  const [showLoadImageModal, setShowLoadImageModal] = useState(false);
  const [showSellConfirmationModal, setShowSellConfirmationModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalActionData, setModalActionData] = useState(null);
  
  // Selected data
  const [selectedData, setSelectedData] = useState(null);
  const [transportData, setTransportData] = useState(null);
  const [transportMode, setTransportMode] = useState('');
  
  const userinfo = JSON.parse(localStorage.getItem('userinfo'));

  // Add a new state to store certificate availability
  const [productCertificates, setProductCertificates] = useState({});
  const [productQRCodes, setProductQRCodes] = useState({});

  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [selectedProductForDocuments, setSelectedProductForDocuments] = useState(null);

  // Add this function to check if a certificate exists for a product
  const checkCertificateAvailability = async (productID) => {
    try {
      const response = await fetch(`${BASE_URL}/checkCertificateExists?product_id=${productID}`);
      
      if (!response.ok) {
        return false;
      }
      
      const data = await response.json();
      return data.exists === true;
    } catch (error) {
      console.error("Error checking certificate:", error);
      return false;
    }
  };

  // Function to load certificate availability for the current products
  const loadCertificateAvailability = async (products) => {
    const certificatePromises = products.map(async (product) => {
      const hasCertificate = await checkCertificateAvailability(product.productID);
      return { productID: product.productID, hasCertificate };
    });
    
    const loadedCertificates = await Promise.all(certificatePromises);
    const certificatesMap = {};
    loadedCertificates.forEach(item => {
      certificatesMap[item.productID] = item.hasCertificate;
    });
    
    setProductCertificates(certificatesMap);
  };
  
  // Fetch data on component mount
  useEffect(() => {
    dispatch(productsByUser());
    dispatch(getProductsList());
    //dispatch(getLocationsList());
    dispatch(getProductStateList());
    dispatch(getProductLotList());
  }, [dispatch, selectedLanguage]);
  
  // Refresh data when language changes
  useEffect(() => {
    dispatch(getProductLotList());
  }, [dispatch, selectedLanguage]);
  
  // Save itemsPerPage to localStorage
  useEffect(() => {
    localStorage.setItem('productsPerPage', itemsPerPage.toString());
  }, [itemsPerPage]);
  
  // Handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter products based on active tab and search
  const getFilteredProducts = () => {
    let filteredProducts = [...dataLot];

    // Filter by tab
    switch (activeTab) {
      case PRODUCT_STATES.DRAFT:
        filteredProducts = filteredProducts.filter(p => p.publishId === 2);
        break;
      case PRODUCT_STATES.ARCHIVED:
        filteredProducts = filteredProducts.filter(p => p.publishId === -1);
        break;
      case PRODUCT_STATES.ACTIVE:
      default:
        filteredProducts = filteredProducts.filter(p => p.publishId !== 2 && p.publishId !== -1);
        break;
    }

    
    // Filter by search term
    if (search) {
      const searchTerm = search.toLowerCase().trim();
      filteredProducts = filteredProducts.filter(product => {
        return Object.values(product).some(value => {
          if (typeof value === 'string') {
            return value.toLowerCase().includes(searchTerm);
          } else if (typeof value === 'number' || typeof value === 'bigint') {
            return value.toString().includes(searchTerm);
          }
          return false;
        });
      });
    }
    
    // Apply sorting
    if (sortColumn) {
      filteredProducts.sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];
        
        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';
        
        // Handle dates
        if (sortColumn === 'createdOn') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // Compare values
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
          return sortDirection === 'asc' ? comparison : -comparison;
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filteredProducts;
  };
  
  // Pagination logic
  const paginatedProducts = () => {
    const filteredProducts = getFilteredProducts();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(startIndex, startIndex + itemsPerPage);
  };
  
  // Action handlers
  const handleRefresh = () => {
    dispatch(getProductLotList());
    toast.info(intl.formatMessage({ id: 'myprods.notifications.refreshed.message' }), {
      ...ToastProperties
    });
  };
  
  const handleView = (data) => {
    setSelectedData(data);
    setShowProductPanel(true);
  };
  
  const handleEdit = (data) => {
    setSelectedData(data);
    setShowCreateModal(true);
  };
  
  const handleLoadImage = (data) => {
    setSelectedData(data);
    setShowLoadImageModal(true);
  };
  
  const handleTransport = (data) => {
    // Validate product_ref_id before allowing transport
    if (!data.product_ref_id) {
      toast.error(intl.formatMessage({ id: 'myprods.notifications.transport.missingProductId' }), {
        ...ToastProperties
      });
      return;
    }
    
    //const locationId = locationList.find(state => state.location === data.location)?.id || null;
    
    const payload = {
      contractID: userinfo.product_contractId,
      sku: data.sku,
      productLotUuid: data.productID,
      location: data.location,
      period: data.period,
      //locationId: locationId,
      product_ref_id: data.product_ref_id,
    };
    
    setTransportData(payload);
    setTransportMode('transport');
    setShowTransportModal(true);
  };
  
  const handleTransform = (data) => {
    //const locationId = locationList.find(state => state.location === data.location)?.id || null;
    const id_category = productsList.find(state => state.label === data.productName)?.id_category || null;
    
    const payload = {
      contractID: userinfo.product_contractId,
      sku: data.sku,
      productLotUuid: data.productID,
      physicalState: data.physicalState,
      location: data.location,
      period: data.period,
      categoryName: data.productName,
      locationId: data.location_id,
      category: id_category,
      product_ref_id: data.product_ref_id,
    };
    
    setTransportData(payload);
    setTransportMode('transform');
    setShowTransportModal(true);
  };
  
  const handlePublishChange = (data) => {
    setSelectedData(data);
    
    if (data.publishId === 0) {
      setShowSellConfirmationModal(true);
    } else {
      publishProduct(data);
    }
  };
  
  const publishProduct = (data) => {
    const obj = {
      ...data,
      contractId: userinfo.product_contractId,
      upc: data.UPC,
      publish: data.publishId === 1 ? 0 : 1,
      state: data.publishId === 0 ? 'ForSale' : 'NotForSale',
    };
    
    dispatch(updateProductDB(obj));
  };
  
  const handleArchive = (data) => {
    setModalActionData(data);
    setShowArchiveModal(true);
  };
  
  const confirmArchive = () => {
    if (modalActionData) {
      const obj = {
        ...modalActionData,
        contractId: userinfo.product_contractId,
        upc: modalActionData.UPC,
        publishId: -1,
        publish: modalActionData.publish,
        state: 'Archived'
      };
      dispatch(archiveLot(obj));
      setShowArchiveModal(false);
    }
  };
  
  const handleUnarchive = (data) => {
    setModalActionData(data);
    setShowUnarchiveModal(true);
  };
  
  const confirmUnarchive = () => {
    if (modalActionData) {
      const obj = {
        ...modalActionData,
        contractId: userinfo.product_contractId,
        upc: modalActionData.UPC,
        publishId: 0,
        publish: 'N',
        state: 'NotForSale'
      };
      dispatch(unarchiveLot(obj));
      setShowUnarchiveModal(false);
    }
  };
  
  const handleDelete = (data) => {
    setModalActionData(data);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = () => {
    if (modalActionData) {
      dispatch(deleteDraftProperties(modalActionData));
      setShowDeleteModal(false);
    }
  };
  
  const downloadCertificate = async (productID, location, sku) => {
    try {
      // First get the certificate
      //const productResponse = await fetch(`${BASE_URL}/getProductCertificate?product_id=${productId}&targetLanguage=${selectedLanguage}`);
      const productResponse = await fetch(`${BASE_URL}/getProductCertificate?product_id=${productID}&location=${encodeURIComponent(location)}&targetLanguage=${selectedLanguage}`);// smart way to avoid using twice google api

      
      if (!productResponse.ok) {
        throw new Error('Error fetching product certificate');
      }
      
      // Open the certificate page using SKU
      const certificateUrl = `${window.location.origin}/certificate/${sku}`;
      window.open(certificateUrl, '_blank');
      
      // Optionally, provide access to the QR code in a separate tab or download
      const qrCodeUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/qrcode/${productID}.png`;
      // You could open this in a new tab or make it downloadable
      // window.open(qrCodeUrl, '_blank');
    } catch (error) {
      console.error('Error:', error.message);
    }
  };
  
  const onSave = (obj, cb) => {
    try {
      dispatch(saveProperties({ payload: obj, cb }));
    } catch (error) {
      console.log(error);
    }
  };
  
  const onDraftSave = (obj, cb) => {
    try {
      dispatch(saveDraftProperties({ payload: obj, cb }));
    } catch (error) {
      console.log(error);
    }
  };
  
  // Render status badge
  const renderStatusBadge = (data) => {
    if (data.publishId === -1) {
      return <Badge color="gray"><FormattedMessage id="status.archived" /></Badge>;
    }
    
    if (data.publishId === 2) {
      return <Badge color="blue"><FormattedMessage id="status.draft" /></Badge>;
    }
    
    return data.publishId === 1
      ? <Badge color="green"><FormattedMessage id="status.forSale" /></Badge>
      : <Badge color="yellow"><FormattedMessage id="status.notForSale" /></Badge>;
  };
  
  // Render primary action button based on product state
  const renderPrimaryAction = (data) => {
    if (data.publishId === -1) {
      return (
        <Button 
          variant="outline" 
          color="blue" 
          size="xs" 
          leftIcon={<IconArchive size={14} />}
          onClick={() => handleUnarchive(data)}
        >
          <FormattedMessage id="action.unarchive" />
        </Button>
      );
    }
    
    if (data.publishId === 2) {
      return (
        <Button 
          variant="outline" 
          color="blue" 
          size="xs" 
          leftIcon={<IconEdit size={14} />}
          onClick={() => handleEdit(data)}
        >
          <FormattedMessage id="action.edit" />
        </Button>
      );
    }
    
    return (
      <Button 
        variant={data.publishId === 1 ? 'outline' : 'filled'}
        color={data.publishId === 1 ? 'orange' : 'green'}
        size="xs" 
        onClick={() => handlePublishChange(data)}
      >
        {data.publishId === 1
          ? <FormattedMessage id="action.withdraw" />
          : <FormattedMessage id="action.publish" />
        }
      </Button>
    );
  };
  
  // Render action menu for each product
  const renderActionMenu = (data) => {
    const isArchived = data.publishId === -1;
    const isDraft = data.publishId === 2;
    
    return (
      <Menu position="bottom-end" shadow="md" withArrow withinPortal>
        <Menu.Target>
          <ActionIcon>
            <IconDotsVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        
        <Menu.Dropdown sx={{ 
          maxHeight: '250px', 
          overflowY: 'auto', 
          zIndex: 1350,
          minWidth: '200px'
        }}>
          <Menu.Item 
            icon={<IconEye size={14} />} 
            onClick={() => handleView(data)}
            sx={{ 
              '& .mantine-Menu-itemInner': { justifyContent: 'flex-start' },
              '& .mantine-Menu-itemIcon': { width: '20px', minWidth: '20px', marginRight: '10px' }
            }}
          >
            <FormattedMessage id="action.view" />
          </Menu.Item>
          
          {!isArchived && !isDraft && (
            <>
              <Menu.Item 
                icon={<IconTruckDelivery size={14} />} 
                onClick={() => handleTransport(data)}
                sx={{ 
                  '& .mantine-Menu-itemInner': { justifyContent: 'flex-start' },
                  '& .mantine-Menu-itemIcon': { width: '20px', minWidth: '20px', marginRight: '10px' }
                }}
              >
                <FormattedMessage id="action.transport" />
              </Menu.Item>
              
              <Menu.Item 
                icon={<IconArrowsLeftRight size={14} />} 
                onClick={() => handleTransform(data)}
                sx={{ 
                  '& .mantine-Menu-itemInner': { justifyContent: 'flex-start' },
                  '& .mantine-Menu-itemIcon': { width: '20px', minWidth: '20px', marginRight: '10px' }
                }}
              >
                <FormattedMessage id="action.transform" />
              </Menu.Item>
              
              <Menu.Item 
                icon={<IconPhoto size={14} />} 
                onClick={() => handleLoadImage(data)}
                sx={{ 
                  '& .mantine-Menu-itemInner': { justifyContent: 'flex-start' },
                  '& .mantine-Menu-itemIcon': { width: '20px', minWidth: '20px', marginRight: '10px' }
                }}
              >
                <FormattedMessage id="action.loadImage" />
              </Menu.Item>
              
              {/* ADD THIS NEW MENU ITEM FOR DOCUMENTS */}
              <Menu.Item 
                icon={<IconFileText size={14} />} 
                onClick={() => openDocumentModal(data)}
                sx={{ 
                  '& .mantine-Menu-itemInner': { justifyContent: 'flex-start' },
                  '& .mantine-Menu-itemIcon': { width: '20px', minWidth: '20px', marginRight: '10px' }
                }}
              >
                <FormattedMessage id="myProducts.manageDocuments" />
              </Menu.Item>
              
              <Menu.Item 
                color="orange"
                icon={<IconArchive size={14} />} 
                onClick={() => handleArchive(data)}
                sx={{ 
                  '& .mantine-Menu-itemInner': { justifyContent: 'flex-start' },
                  '& .mantine-Menu-itemIcon': { width: '20px', minWidth: '20px', marginRight: '10px' }
                }}
              >
                <FormattedMessage id="action.archive" />
              </Menu.Item>
            </>
          )}
          
          {isDraft && (
            <Menu.Item 
              color="red"
              icon={<IconTrash size={14} />} 
              onClick={() => handleDelete(data)}
              sx={{ 
                '& .mantine-Menu-itemInner': { justifyContent: 'flex-start' },
                '& .mantine-Menu-itemIcon': { width: '20px', minWidth: '20px', marginRight: '10px' }
              }}
            >
              <FormattedMessage id="action.delete" />
            </Menu.Item>
          )}
          
          {data.certificate && (
            <Menu.Item 
              icon={<IconDownload size={14} />} 
              onClick={() => downloadCertificate(data.productID, data.location, data.sku)}
              sx={{ 
                '& .mantine-Menu-itemInner': { justifyContent: 'flex-start' },
                '& .mantine-Menu-itemIcon': { width: '20px', minWidth: '20px', marginRight: '10px' }
              }}
            >
              <FormattedMessage id="action.downloadCertificate" />
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    );
  };
  
  // Render table view
  const renderTableView = () => {
    const products = paginatedProducts();
    
    const renderSortIcon = (column) => {
      if (sortColumn !== column) {
        return <IconSelector size={14} style={{ marginLeft: 4, opacity: 0.5 }} />;
      }
      return sortDirection === 'asc' 
        ? <IconChevronUp size={14} style={{ marginLeft: 4 }} />
        : <IconChevronDown size={14} style={{ marginLeft: 4 }} />;
    };
    
    return (
      <ScrollArea>
        <Table striped highlightOnHover withBorder>
          <thead>
            <tr>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('productName')}
              >
                <Group spacing={4} noWrap>
                  <FormattedMessage id="table.product" />
                  {renderSortIcon('productName')}
                </Group>
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('sku')}
              >
                <Group spacing={4} noWrap>
                  <FormattedMessage id="table.sku" />
                  {renderSortIcon('sku')}
                </Group>
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('publishId')}
              >
                <Group spacing={4} noWrap>
                  <FormattedMessage id="table.status" />
                  {renderSortIcon('publishId')}
                </Group>
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('location')}
              >
                <Group spacing={4} noWrap>
                  <FormattedMessage id="table.location" />
                  {renderSortIcon('location')}
                </Group>
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('createdOn')}
              >
                <Group spacing={4} noWrap>
                  <FormattedMessage id="table.createdOn" />
                  {renderSortIcon('createdOn')}
                </Group>
              </th>
              <th><FormattedMessage id="table.certificate" /></th>
              <th style={{ width: 180 }}><FormattedMessage id="table.actions" /></th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <Center p="xl">
                    <Text color="dimmed">
                      <FormattedMessage id={`table.noProducts.${activeTab}`} />
                    </Text>
                  </Center>
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr key={product.productID}>
                  <td style={{ maxWidth: '250px' }}>
                    <Group spacing={8} align="center" noWrap>
                      {product.imagePath ? (
                        <Avatar 
                          src={product.imagePath} 
                          size={30}
                          radius="sm"
                        />
                      ) : (
                        <Avatar size={30} radius="sm" color="blue">
                          {product.productName?.charAt(0) || 'P'}
                        </Avatar>
                      )}
                      <Text 
                        size="sm" 
                        style={{ 
                          margin: 0, 
                          padding: 0, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer' 
                        }}
                        title={`${product.productName}${product.productNotes ? ' - ' + product.productNotes : ''}`}
                      >
                        <span style={{ fontWeight: 500 }}>{product.productName}</span>
                        {' - '}
                        <span style={{ color: '#868e96' }}>
                          {product.productNotes || '-'}
                        </span>
                      </Text>
                    </Group>
                  </td>
                  <td>
                    <Text size="sm">{product.sku || '-'}</Text>
                  </td>
                  <td>
                    {renderStatusBadge(product)}
                  </td>
                  <td>
                    <Text size="sm">{product.location || '-'}</Text>
                  </td>
                  <td>
                    <Text size="sm">
                      {moment(product.createdOn).format('DD-MM-YYYY') || '-'}
                    </Text>
                  </td>
                  <td>
                    <Center>
                      <Group spacing="xs">
                        {productCertificates[product.productID] && (
                          <ActionIcon 
                            color="blue" 
                            onClick={() => downloadCertificate(product.productID, product.location, product.sku)}
                            title={intl.formatMessage({ id: 'tooltip.certificate' })}
                          >
                            <FontAwesomeIcon icon={faFilePdf} />
                          </ActionIcon>
                        )}
                        
                        {productQRCodes[product.productID] && (
                          <ActionIcon 
                            color="green" 
                            onClick={() => downloadQRCode(product.productID)}
                            title={intl.formatMessage({ id: 'myprods.tooltip.qrcode' })}
                          >
                            <IconQrcode size={16} />
                          </ActionIcon>
                        )}
                        
                        {/* Add the external link icon for certificate link */}
                        <Tooltip 
                          label={intl.formatMessage({ id: 'myprods.tooltip.certificateLink' })}
                          withArrow
                          position="top"
                          styles={(theme) => ({
                            tooltip: {
                              backgroundColor: theme.colors.blue[6],
                              color: theme.white,
                              maxHeight: '40px',
                              padding: '4px 8px',
                              fontSize: '12px'
                            }
                          })}
                        >
                          <ActionIcon
                            component="a"
                            href={`/certificate/${product.sku}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="blue"
                            variant="light"
                            size="sm"
                          >
                            <IconExternalLink size={16} />
                          </ActionIcon>
                        </Tooltip>
                        
                        {!productCertificates[product.productID] && !productQRCodes[product.productID] && '-'}
                      </Group>
                    </Center>
                  </td>
                  <td>
                    <Group position="right" spacing="xs">
                      {renderPrimaryAction(product)}
                      <ActionIcon 
                        color="blue" 
                        onClick={() => handleView(product)}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                      {renderActionMenu(product)}
                    </Group>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </ScrollArea>
    );
  };

  // Update the useEffect to load images when products change
  useEffect(() => {
    const products = paginatedProducts();
    if (products.length > 0) {
      loadCertificateAvailability(products);
      loadQRCodeAvailability(products);
    }
  }, [currentPage, search, activeTab, dataLot]);
  
  // Render card view
  const renderCardView = () => {
    const products = paginatedProducts();
    
    if (products.length === 0) {
      return (
        <Center p="xl">
          <Text color="dimmed">
            <FormattedMessage id={`table.noProducts.${activeTab}`} />
          </Text>
        </Center>
      );
    }
    
    return (
      <Grid>
        {products.map((product) => (
          <Grid.Col xs={12} sm={6} md={4} key={product.productID}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Card.Section>
                <ProductCardImage product={product} />
              </Card.Section>
              <Group position="apart" mt="md" mb="xs">
                <Text weight={500}>{product.productName}</Text>
                {renderStatusBadge(product)}
              </Group>
              
              <Text size="sm" color="dimmed" mb="md" lineClamp={2}>
                {product.productNotes || '-'}
              </Text>
              <Group position="apart" spacing="xs">
                <Text size="xs">
                  <b><FormattedMessage id="card.sku" />:</b> {product.sku || '-'}
                </Text>
                <Text size="xs">
                  <b><FormattedMessage id="card.location" />:</b> {product.location || '-'}
                </Text>
              </Group>
              
              <Divider my="sm" />
              
              <Group position="apart">
                {renderPrimaryAction(product)}
                
                <Group spacing={5}>
                {productCertificates[product.productID] && (
                    <ActionIcon 
                      color="blue" 
                      onClick={() => downloadCertificate(product.productID, product.location, product.sku)}
                    >
                      <FontAwesomeIcon icon={faFilePdf} />
                    </ActionIcon>
                )}
                  
                    <ActionIcon 
                      color="blue" 
                      onClick={() => handleView(product)}
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                  {renderActionMenu(product)}
              </Group>
              </Group>
            </Card>
          </Grid.Col>
        ))}
      </Grid>
    );
  };

  const renderSummary = () => {
    const totalProducts = dataLot.length;
    const activeProducts = dataLot.filter(p =>  p.publishId !== 2 && p.publishId !== -1).length;
    const draftProducts = dataLot.filter(p =>  p.publishId === 2).length;
    const archivedProducts = dataLot.filter(p => p.publishId === -1).length;
    const forSaleProducts = dataLot.filter(p => p.publishId === 1).length;
    
    return (
      <Grid mb="md">
        <Grid.Col span={3}>
          <Paper p="md" withBorder>
            <Text size="lg" weight={700} color="blue">
              {totalProducts}
            </Text>
            <Text size="sm" color="dimmed">
              <FormattedMessage id="summary.totalProducts" />
            </Text>
          </Paper>
        </Grid.Col>
        <Grid.Col span={3}>
          <Paper p="md" withBorder>
            <Text size="lg" weight={700} color="green">
              {forSaleProducts}
            </Text>
            <Text size="sm" color="dimmed">
              <FormattedMessage id="summary.forSale" />
            </Text>
          </Paper>
        </Grid.Col>
        <Grid.Col span={3}>
          <Paper p="md" withBorder>
            <Text size="lg" weight={700} color="blue">
              {draftProducts}
            </Text>
            <Text size="sm" color="dimmed">
              <FormattedMessage id="summary.drafts" />
            </Text>
          </Paper>
        </Grid.Col>
        <Grid.Col span={3}>
          <Paper p="md" withBorder>
            <Text size="lg" weight={700} color="gray">
              {archivedProducts}
            </Text>
            <Text size="sm" color="dimmed">
              <FormattedMessage id="summary.archived" />
            </Text>
          </Paper>
        </Grid.Col>
      </Grid>
    );
  };

  const checkQRCodeAvailability = async (productID) => {
    try {
      const qrCodeUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/qrcode/${productID}.png`;
      const response = await fetch(qrCodeUrl, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error("Error checking QR code:", error);
      return false;
    }
  };

  // Load QR code availability for products
  const loadQRCodeAvailability = async (products) => {
    const qrPromises = products.map(async (product) => {
      const hasQRCode = await checkQRCodeAvailability(product.productID);
      return { productID: product.productID, hasQRCode };
    });
    
    const loadedQRCodes = await Promise.all(qrPromises);
    const qrCodesMap = {};
    loadedQRCodes.forEach(item => {
      qrCodesMap[item.productID] = item.hasQRCode;
    });
    
    setProductQRCodes(qrCodesMap);
  };

  // Download QR code function
  const downloadQRCode = async (productID) => {
    try {
      const qrCodeUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/qrcode/${productID}.png`;
      
      // Check if QR code exists
      const response = await fetch(qrCodeUrl, { method: 'HEAD' });
      
      if (response.ok) {
        // Create a temporary link to download the QR code
        const link = document.createElement('a');
        link.href = qrCodeUrl;
        link.download = `qrcode-${productID}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(intl.formatMessage({ id: 'myprods.notifications.qrcode.downloaded' }), {
          ...ToastProperties
        });
      } else {
        toast.error(intl.formatMessage({ id: 'myprods.notifications.qrcode.notAvailable' }), {
          ...ToastProperties
        });
      }
    } catch (error) {
      console.error('Error downloading QR code:', error);
      toast.error(intl.formatMessage({ id: 'myprods.notifications.qrcode.error' }), {
        ...ToastProperties
      });
    }
  };

  const openDocumentModal = (product) => {
    setSelectedProductForDocuments(product);
    setDocumentModalOpen(true);
  };
  
  return (
    <Box p="md">
      <Group position="apart" mb="lg">
        <Title order={2}>
          <FormattedMessage id="productManagement.title" />
        </Title>
        
        <Group>
          {/* <Tooltip label={intl.formatMessage({ id: 'tooltip.refresh' })}> */}
            <ActionIcon
              color="blue"
              variant="subtle"
              onClick={handleRefresh}
            >
              <IconRefresh size={20} />
            </ActionIcon>
          {/* </Tooltip> */}
          
          <Button
            leftIcon={<IconPlus size={16} />}
            onClick={() => setShowCreateModal(true)}
          >
            <FormattedMessage id="button.createProduct" />
          </Button>
        </Group>
      </Group>
      
      {renderSummary()}
      
      <Tabs 
        value={activeTab} 
        onTabChange={setActiveTab} 
        mb="md"
        styles={(theme) => ({
          tab: {
            '&[data-active]': {
              backgroundColor: theme.colors.blue[0],
              borderColor: theme.colors.blue[6],
              fontWeight: 600,
            },
            '&:hover': {
              backgroundColor: theme.colors.gray[0],
            },
          },
        })}
      >
        <Tabs.List>
          <Tabs.Tab 
            value={PRODUCT_STATES.ACTIVE} 
            icon={<IconFileText size={14} />}
          >
            <FormattedMessage id="tabs.activeProducts" />
          </Tabs.Tab>
          <Tabs.Tab 
            value={PRODUCT_STATES.DRAFT} 
            icon={<IconEdit size={14} />}
          >
            <FormattedMessage id="tabs.draftProducts" />
          </Tabs.Tab>
          <Tabs.Tab 
            value={PRODUCT_STATES.ARCHIVED} 
            icon={<IconArchive size={14} />}
          >
            <FormattedMessage id="tabs.archivedProducts" />
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>
      
      <Paper mb="md" p="md" withBorder>
        <Group position="apart">
          <TextInput
            placeholder={intl.formatMessage({ id: 'search.placeholder' })}
            icon={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ width: '60%' }}
          />
          
          <Group>
            <Button 
              variant="subtle" 
              leftIcon={<IconFilter size={16} />}
              color="gray"
            >
              <FormattedMessage id="button.advancedFilters" />
            </Button>
            
            <SegmentedControl
              value={viewMode}
              onChange={setViewMode}
              data={[
                { 
                  value: VIEW_MODES.TABLE, 
                  label: <Center><IconLayoutList size={16} /></Center>
                },
                { 
                  value: VIEW_MODES.CARDS, 
                  label: <Center><IconLayoutGrid size={16} /></Center>
                }
              ]}
            />
          </Group>
        </Group>
      </Paper>
      
      {/* Content area */}
      <Paper p="md" withBorder>
        {viewMode === VIEW_MODES.TABLE ? renderTableView() : renderCardView()}
        
        {/* Pagination */}
        {getFilteredProducts().length > itemsPerPage && (
          <Group position="apart" mt="md">
            <Group spacing="xs" align="center">
              <Text size="sm" color="dimmed">
                <FormattedMessage id="table.rowsPerPage" defaultMessage="Rows per page:" />
              </Text>
              <NativeSelect
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1); // Reset to first page when changing items per page
                }}
                data={[
                  { value: '10', label: '10' },
                  { value: '25', label: '25' },
                  { value: '50', label: '50' },
                  { value: '100', label: '100' }
                ]}
                size="xs"
                style={{ width: 80 }}
              />
            </Group>
            <Pagination
              total={Math.ceil(getFilteredProducts().length / itemsPerPage)}
              value={currentPage}
              onChange={setCurrentPage}
              withEdges
            />
          </Group>
        )}
      </Paper>
      
      {/* Modals */}
      {selectedData && (
        <ProductPanel
          modal={showProductPanel}
          toggle={() => setShowProductPanel(false)}
          readonly={true}
          value={selectedData}
        />
      )}
      
      {transportData && (
        <TransportationModal
          modal={showTransportModal}
          toggle={() => setShowTransportModal(false)}
          title={
            transportMode === 'transform' 
              ? <FormattedMessage id="myprods.xtratitle.transform" />
              : <FormattedMessage id="myprods.xtratitle.transport" />
          }
          data={transportData}
          tag={transportMode === 'transform' ? 'TRANSFORM_ACTION' : 'TRANSPORT_ACTION'}
          onSave={(obj, cb) => { if (cb) cb(); }}
          dataLot={dataLot}
        />
      )}
      
      {selectedData && (
        <ImageUploadModal
          modal={showLoadImageModal}
          toggle={() => setShowLoadImageModal(false)}
          selectedData={selectedData}
        />
      )}
      
      {selectedData && (
        <RemoveFieldConfirmationModal
          fieldName={selectedData}
          onConfirm={() => {
            publishProduct(selectedData);
            setShowSellConfirmationModal(false);
          }}
          onCancel={() => setShowSellConfirmationModal(false)}
          opened={showSellConfirmationModal}
        />
      )}
      
      <CreatePropertiesModal
        modal={showCreateModal}
        toggle={() => setShowCreateModal(false)}
        onSave={(obj, cb) => onSave(obj, cb)}
        onDraftSave={(obj, cb) => onDraftSave(obj, cb)}
      />
      
      {/* Reusing RemoveFieldConfirmationModal for our confirmation dialogs */}
      <RemoveFieldConfirmationModal
        fieldName={{ name: <FormattedMessage id="modals.archive.title" /> }}
        message={<FormattedMessage id="modals.archive.message" />}
        onConfirm={confirmArchive}
        onCancel={() => setShowArchiveModal(false)}
        opened={showArchiveModal}
        confirmText={<FormattedMessage id="modals.archive.confirm" />}
        cancelText={<FormattedMessage id="modals.archive.cancel" />}
      />
      
      <RemoveFieldConfirmationModal
        fieldName={{ name: <FormattedMessage id="modals.unarchive.title" /> }}
        message={<FormattedMessage id="modals.unarchive.message" />}
        onConfirm={confirmUnarchive}
        onCancel={() => setShowUnarchiveModal(false)}
        opened={showUnarchiveModal}
        confirmText={<FormattedMessage id="modals.unarchive.confirm" />}
        cancelText={<FormattedMessage id="modals.unarchive.cancel" />}
      />
      
      <RemoveFieldConfirmationModal
        fieldName={{ name: <FormattedMessage id="modals.delete.title" /> }}
        message={<FormattedMessage id="modals.delete.message" />}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
        opened={showDeleteModal}
        confirmText={<FormattedMessage id="modals.delete.confirm" />}
        cancelText={<FormattedMessage id="modals.delete.cancel" />}
      />
      
      <DocumentUploadModal
        opened={documentModalOpen}
        onClose={() => setDocumentModalOpen(false)}
        selectedData={selectedProductForDocuments}
      />
    </Box>
  );
}